
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Box, Typography, Paper, Button, Stack, Backdrop, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LogOnMount } from '@mightyhive/analytics';
import { useTheme } from '@mui/material/styles';
import { apiFetch } from '../utils/api';
import { getUploadErrorMessage, uploadHypeFiles } from '../utils/hypeUpload';
import { useHype } from '../context/HypeContext';
import { useUser } from '../context/UserContext';

type FileList = {
  fileName: string,
  fileId: string
}

export function Hype(): JSX.Element {
  const theme = useTheme();
  const [statusMessage, setStatusMessage] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fileList, setFileList] = useState<FileList[]>([])

  const { isPoweruser } = useUser()
  const {vectorStore} = useHype()
  const {vectorStoreId, vectorStoreName = ""} = vectorStore

  const fetchFileList = async () => {
    const response = await apiFetch(`/api/hype/list_files`, { method: 'POST' });
    if (!response.ok) throw new Error(`Failed to fetch file list: ${response.status}`);
    const data = await response.json();
    setFileList(data.data.file_list);
  };

  const onDrop = useCallback(async (files: File[]) => {
    try{
      setStatusMessage("")
      setError("")
      setIsLoading(true)
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append('files', file, file.name);
      });
      await uploadHypeFiles(formData)
      setStatusMessage("Files Successfully Uploaded")
      setTimeout(() => setStatusMessage(""), 3000)
      fetchFileList().catch(console.error)
    }catch(ex){
      console.error(ex)
      setError(getUploadErrorMessage(ex))
    }finally{
       setIsLoading(false)
    }
   
  }, []);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'application/json': ['.json'],
      'text/markdown': ['.md'],
      'text/plain': ['.txt'],
    },
  });

  const listFiles = async () => {
    setIsLoading(true)
    try{
      await fetchFileList()
    }catch(ex){
      console.error(ex)
    }finally{
      setIsLoading(false)
    }
  }

  const deleteFile = async (fileId: string) => {
    setIsLoading(true)
    try{
      const response = await apiFetch(`/api/hype/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({"fileId": fileId}),
      })

    // The delete endpoint returns only delete_status, so drop the deleted row from local state.
    if (response.ok) {
      setFileList((prev) => prev.filter((file) => file.fileId !== fileId))
      setStatusMessage("File Successfully Deleted")
      setTimeout(() => setStatusMessage(""), 3000)
    } else {
      setError("Failed to delete file")
    }
    }catch(ex){
      console.error(ex)
    }
    finally{
       setIsLoading(false)
    }
  }
  
  if (!isPoweruser) {
    return (
      <Box>
        <LogOnMount eventName="Page View: Hype" />
        <Alert severity="warning">Hype knowledge base management is only available to powerusers.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <LogOnMount eventName="Page View: Hype" />
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        Hype Knowledge Base File Upload
      </Typography>
      <Box sx={{margin: '20px auto' }}>
        <Backdrop
          sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
          {/* Container wrapper for the Drop Zone Area */}
          <Paper
            {...getRootProps()}
            elevation={isDragActive ? 4 : 1}
            sx={{
              padding: 4,
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragActive ? '#f0f7ff' : '#fafafa',
              borderColor: isDragActive ? 'primary.main' : '#ccc',
              borderStyle: 'dashed',
              borderWidth: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                background: '#fcfcfc',
              },
            }}
          >
            <input {...getInputProps()} disabled={isLoading || !vectorStoreId}/>
            <CloudUploadIcon color={isDragActive ? "primary" : "action"} sx={{ fontSize: 48, mb: 1 }} />
            
            {isDragActive ? (
              <Typography color="primary" variant="body1">Drop your files here...</Typography>
            ) : (
              <Typography variant="body1">
                Drag & drop files here, or click to select files
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Supports PDF, MD, JSON, and TXT files
            </Typography>
          </Paper>
        </Box>
        <Box>
          <Paper style={{padding: "10px"}}>
            <Stack direction={"row"} justifyContent={"space-between"} padding={'5px'}>
              <div>
                <div>Vector Store Name: {vectorStoreName}</div>
                <div>Vector Store ID: {vectorStoreId}</div>
              </div>
            
              <Button onClick={listFiles} disabled={!vectorStoreId || isLoading}>
                View Existing Files
              </Button>
            </Stack>
             {fileList && 
              <div>
                {
                fileList.map((fileDetails: FileList, i: number) => {
                  const isEven = i % 2 === 0
                  return (
                    <div key={`${fileDetails.fileId}`} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0px 5px', backgroundColor: isEven ? '#ede4ff' : '#fff'}}>
                      <div>{fileDetails.fileName}</div>
                      <Button disabled={isLoading} onClick={()=>deleteFile(fileDetails.fileId)}>Delete File</Button>
                    </div>
                )})}
            </div>}
          </Paper>
        </Box>
        {error && <span style={{color: 'red'}}>{error}</span>}
        <span>{statusMessage}</span>
    </Box>
  );
}
